import React, { useEffect, useState } from "react";
import { Hash, Bot, Globe2, PhoneCall, ArrowRight, CheckCircle2, Loader2, Phone, PhoneIncoming, PhoneOutgoing, Sparkles } from "lucide-react";

/**
 * VoiceCallingFlow — animated 4-step "how AI voice calling works" section.
 * Auto-cycles through Buy Number → Create Agent → Select Country → Place Calls
 * with a live preview of each step on the right.
 */
const STEPS = [
  {
    id: "number",
    short: "01",
    label: "Buy Twilio Number",
    desc: "Pick a real local number in 100+ countries. We connect it to your agent in one click.",
    Icon: Hash,
    bg: "#FFF7ED",
    ink: "#EA580C",
  },
  {
    id: "agent",
    short: "02",
    label: "Create Agent & Train",
    desc: "Drop your website URL + brand voice. Your KB is auto-trained in under 90 seconds.",
    Icon: Bot,
    bg: "#FEF3C7",
    ink: "#D97706",
  },
  {
    id: "country",
    short: "03",
    label: "Select Calling Country",
    desc: "Choose the markets you want to dial. Compliance + caller-ID set automatically.",
    Icon: Globe2,
    bg: "#FFEDD5",
    ink: "#F97316",
  },
  {
    id: "calls",
    short: "04",
    label: "Inbound + Outbound Calls",
    desc: "Agent answers incoming calls instantly. Bulk-dial outbound campaigns from CSV.",
    Icon: PhoneCall,
    bg: "#FFE4D6",
    ink: "#C2410C",
  },
];

export default function VoiceCallingFlow() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setActive((a) => (a + 1) % STEPS.length), 3800);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="relative py-24 lg:py-32 bg-[#0F1115] text-white overflow-hidden" data-testid="voice-calling-flow">
      {/* warm radial glow */}
      <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full blur-3xl opacity-30 pointer-events-none"
           style={{ background: "radial-gradient(circle, #F97316 0%, transparent 70%)" }} />
      <div className="absolute -bottom-32 -right-32 w-[520px] h-[520px] rounded-full blur-3xl opacity-25 pointer-events-none"
           style={{ background: "radial-gradient(circle, #FBBF24 0%, transparent 70%)" }} />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="font-mono text-xs text-[#FBBF24]/80 mb-3 tracking-wider">// 05 · ai voice calling</div>
        <h2 className="font-display font-extrabold text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight max-w-3xl">
          Your phone <span className="bg-gradient-to-r from-[#FBBF24] via-[#F97316] to-[#EA580C] bg-clip-text text-transparent">never sleeps.</span><br />
          Your team can.
        </h2>
        <p className="text-white/60 mt-5 max-w-2xl text-base md:text-lg">
          Inbound and outbound AI calling, live in four steps. No telecom contracts. No dev work.
        </p>

        <div className="mt-14 grid lg:grid-cols-12 gap-8 items-start">
          {/* LEFT — step list */}
          <div className="lg:col-span-5 space-y-3" data-testid="voice-flow-steps">
            {STEPS.map((s, i) => {
              const isActive = i === active;
              const isDone = i < active;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setActive(i)}
                  data-testid={`voice-flow-step-${i}`}
                  className={`w-full text-left flex items-start gap-4 p-5 rounded-[10px] border transition-all duration-500 ${
                    isActive
                      ? "border-[#F97316] bg-gradient-to-br from-[#F97316]/15 via-[#F97316]/8 to-transparent shadow-[0_12px_40px_-12px_rgba(249,115,22,0.5)]"
                      : "border-white/10 bg-white/[0.02] hover:border-white/20"
                  }`}
                >
                  <div
                    className={`flex-shrink-0 w-11 h-11 rounded-[8px] flex items-center justify-center font-mono text-xs font-bold transition-all duration-500 ${
                      isActive
                        ? "bg-gradient-to-br from-[#FBBF24] to-[#EA580C] text-white scale-110"
                        : isDone
                        ? "bg-[#F97316]/20 text-[#FBBF24]"
                        : "bg-white/5 text-white/40"
                    }`}
                  >
                    {isDone ? <CheckCircle2 className="w-5 h-5" /> : s.short}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`font-display font-bold text-lg leading-tight transition-colors duration-500 ${
                      isActive ? "text-white" : "text-white/80"
                    }`}>
                      {s.label}
                    </div>
                    <div className={`text-sm mt-1.5 leading-snug transition-all duration-500 ${
                      isActive ? "text-white/75" : "text-white/40"
                    }`}>
                      {s.desc}
                    </div>
                  </div>
                  <s.Icon className={`flex-shrink-0 w-5 h-5 mt-1 transition-colors duration-500 ${
                    isActive ? "text-[#F97316]" : "text-white/30"
                  }`} />
                </button>
              );
            })}
          </div>

          {/* RIGHT — animated preview screen */}
          <div className="lg:col-span-7" data-testid="voice-flow-preview">
            <div className="relative rounded-[14px] bg-gradient-to-br from-white via-[#FFFBEB] to-[#FFEDD5] border border-[#F97316]/30 shadow-[0_30px_80px_-20px_rgba(249,115,22,0.45)] overflow-hidden min-h-[460px]">
              {/* faux toolbar */}
              <div className="flex items-center gap-1.5 px-5 py-3 border-b border-black/5 bg-white/70 backdrop-blur">
                <span className="w-2.5 h-2.5 rounded-full bg-[#EF4444]/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#10B981]/70" />
                <span className="ml-3 font-mono text-[10px] text-[#4B5563] uppercase tracking-widest">
                  hellosir.ai / calling / {STEPS[active].id}
                </span>
              </div>

              <div className="relative p-7 md:p-10 text-[#0F1115]">
                {STEPS.map((s, i) => (
                  <div
                    key={s.id}
                    className={`transition-all duration-700 ${
                      i === active ? "opacity-100 translate-y-0 relative" : "opacity-0 translate-y-3 absolute inset-0 p-7 md:p-10 pointer-events-none"
                    }`}
                  >
                    {i === 0 && <PreviewBuyNumber />}
                    {i === 1 && <PreviewCreateAgent />}
                    {i === 2 && <PreviewCountry />}
                    {i === 3 && <PreviewCalls />}
                  </div>
                ))}
              </div>
            </div>

            {/* progress bar */}
            <div className="mt-5 flex items-center gap-2" aria-hidden>
              {STEPS.map((_, i) => (
                <span
                  key={i}
                  className={`h-1 rounded-full flex-1 overflow-hidden bg-white/10`}
                >
                  <span
                    className={`block h-full rounded-full bg-gradient-to-r from-[#FBBF24] to-[#F97316] transition-all duration-700 ${
                      i < active ? "w-full" : i === active ? "w-full animate-[hsfill_3.6s_linear_forwards]" : "w-0"
                    }`}
                  />
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes hsfill { from { width: 0%; } to { width: 100%; } }
        @keyframes hspulse { 0%,100% { transform: scale(1); opacity: 0.4; } 50% { transform: scale(1.15); opacity: 0.9; } }
        .hs-pulse { animation: hspulse 1.6s ease-in-out infinite; }
        @keyframes hsdial { 0%,100% { opacity: .35; } 50% { opacity: 1; } }
        .hs-dial { animation: hsdial 1.2s ease-in-out infinite; }
      `}</style>
    </section>
  );
}

// ---------- Per-step preview components ----------

function PreviewBuyNumber() {
  const numbers = [
    { flag: "🇺🇸", code: "+1 (415) 213-9844", city: "San Francisco", price: "$1.15/mo" },
    { flag: "🇬🇧", code: "+44 20 4538 7711", city: "London",         price: "£1.00/mo", pick: true },
    { flag: "🇦🇪", code: "+971 4 333 0290",   city: "Dubai",          price: "$3.50/mo" },
    { flag: "🇮🇳", code: "+91 22 4890 1117",   city: "Mumbai",         price: "$2.00/mo" },
  ];
  return (
    <div>
      <div className="text-xs font-mono text-[#4B5563] uppercase tracking-widest mb-3">// step 01</div>
      <h3 className="font-display font-extrabold text-3xl mb-2">Buy a Twilio number</h3>
      <p className="text-sm text-[#4B5563] mb-6">100+ countries · local & toll-free · instant.</p>
      <div className="space-y-2">
        {numbers.map((n, i) => (
          <div
            key={i}
            className={`flex items-center gap-3 p-3 rounded-[8px] border transition-colors ${
              n.pick ? "border-[#F97316] bg-[#FFF7ED]" : "border-[#E2E8F0] bg-white"
            }`}
          >
            <span className="text-xl">{n.flag}</span>
            <div className="flex-1 min-w-0">
              <div className="font-mono text-sm font-semibold tabular-nums">{n.code}</div>
              <div className="text-[11px] text-[#4B5563]">{n.city}</div>
            </div>
            <div className="font-mono text-xs text-[#4B5563]">{n.price}</div>
            {n.pick && (
              <span className="text-[10px] font-bold uppercase tracking-widest text-white bg-[#F97316] px-2 py-1 rounded-[4px] hs-dial">
                buy
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function PreviewCreateAgent() {
  return (
    <div>
      <div className="text-xs font-mono text-[#4B5563] uppercase tracking-widest mb-3">// step 02</div>
      <h3 className="font-display font-extrabold text-3xl mb-2">Create agent &amp; train</h3>
      <p className="text-sm text-[#4B5563] mb-6">Auto-crawl your site. Auto-write the system prompt.</p>

      <div className="rounded-[8px] border border-[#E2E8F0] bg-white p-4 mb-3">
        <div className="font-mono text-[10px] text-[#4B5563] uppercase tracking-widest mb-1.5">website</div>
        <div className="flex items-center justify-between">
          <span className="font-mono text-sm text-[#0F1115]">https://acme-dental.com</span>
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#10B981] inline-flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> 32 pages
          </span>
        </div>
      </div>

      <div className="rounded-[8px] border border-[#F97316]/40 bg-gradient-to-br from-white to-[#FFF7ED] p-4 mb-3">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-[#F97316]" />
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#EA580C]">drafting system prompt</div>
        </div>
        <div className="space-y-1.5">
          <div className="h-2 rounded bg-[#F97316]/30" style={{ width: "92%" }} />
          <div className="h-2 rounded bg-[#F97316]/20" style={{ width: "78%" }} />
          <div className="h-2 rounded bg-[#F97316]/15" style={{ width: "85%" }} />
        </div>
      </div>

      <div className="flex items-center gap-2 text-xs font-mono text-[#4B5563]">
        <Loader2 className="w-3.5 h-3.5 animate-spin text-[#F97316]" /> KB embedding · 1,284 chunks indexed
      </div>
    </div>
  );
}

function PreviewCountry() {
  const countries = [
    { flag: "🇺🇸", name: "United States",   sel: true },
    { flag: "🇬🇧", name: "United Kingdom",  sel: true },
    { flag: "🇨🇦", name: "Canada",          sel: true },
    { flag: "🇦🇺", name: "Australia",       sel: false },
    { flag: "🇦🇪", name: "United Arab Emirates", sel: true },
    { flag: "🇮🇳", name: "India",           sel: false },
    { flag: "🇩🇪", name: "Germany",         sel: false },
    { flag: "🇸🇬", name: "Singapore",       sel: true },
  ];
  return (
    <div>
      <div className="text-xs font-mono text-[#4B5563] uppercase tracking-widest mb-3">// step 03</div>
      <h3 className="font-display font-extrabold text-3xl mb-2">Select calling country</h3>
      <p className="text-sm text-[#4B5563] mb-6">Compliance + caller-ID auto-handled per region.</p>
      <div className="grid grid-cols-2 gap-2">
        {countries.map((c, i) => (
          <div
            key={i}
            className={`flex items-center gap-2 p-3 rounded-[8px] border transition-colors ${
              c.sel ? "border-[#F97316] bg-[#FFF7ED]" : "border-[#E2E8F0] bg-white"
            }`}
          >
            <span className="text-lg">{c.flag}</span>
            <span className="text-sm font-medium text-[#0F1115] flex-1 truncate">{c.name}</span>
            <span
              className={`w-4 h-4 rounded-full border-2 ${
                c.sel ? "bg-[#F97316] border-[#F97316]" : "border-[#E2E8F0]"
              }`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function PreviewCalls() {
  return (
    <div>
      <div className="text-xs font-mono text-[#4B5563] uppercase tracking-widest mb-3">// step 04</div>
      <h3 className="font-display font-extrabold text-3xl mb-2">Inbound &amp; outbound calls</h3>
      <p className="text-sm text-[#4B5563] mb-6">Agent picks up 24/7. Bulk-dial campaigns from CSV.</p>

      {/* live call card */}
      <div className="rounded-[10px] bg-[#0F1115] text-white p-5 mb-3 relative overflow-hidden">
        <div className="absolute -right-8 -top-8 w-28 h-28 rounded-full blur-2xl opacity-50"
             style={{ background: "radial-gradient(circle, #F97316 0%, transparent 70%)" }} />
        <div className="relative flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-[#10B981]/20 border-2 border-[#10B981] flex items-center justify-center hs-pulse">
            <PhoneIncoming className="w-5 h-5 text-[#10B981]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">incoming · 00:42</div>
            <div className="font-mono text-sm font-semibold tabular-nums">+1 (415) 555-9821</div>
            <div className="text-xs text-white/60 mt-0.5">"Yes, we have an opening tomorrow at 2 PM…"</div>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-1 bg-[#10B981] rounded-full hs-dial" style={{ height: 8 }} />
            <span className="w-1 bg-[#10B981] rounded-full hs-dial" style={{ height: 14, animationDelay: ".2s" }} />
            <span className="w-1 bg-[#10B981] rounded-full hs-dial" style={{ height: 20, animationDelay: ".4s" }} />
            <span className="w-1 bg-[#10B981] rounded-full hs-dial" style={{ height: 12, animationDelay: ".6s" }} />
          </div>
        </div>
      </div>

      {/* outbound campaign progress */}
      <div className="rounded-[10px] border border-[#E2E8F0] bg-white p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <PhoneOutgoing className="w-4 h-4 text-[#F97316]" />
            <div className="font-mono text-[11px] uppercase tracking-widest text-[#0F1115]">outbound campaign · q1-leads</div>
          </div>
          <div className="font-mono text-xs font-semibold tabular-nums text-[#0F1115]">218 / 500</div>
        </div>
        <div className="h-1.5 bg-[#F1F5F9] rounded overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#FBBF24] to-[#F97316] rounded" style={{ width: "43.6%" }} />
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4">
          <Stat label="Connected" value="174" tone="text-[#10B981]" />
          <Stat label="Voicemail" value="29"  tone="text-[#F59E0B]" />
          <Stat label="No answer" value="15"  tone="text-[#94A3B8]" />
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-[6px] border border-[#E2E8F0] bg-[#F9FAFB] px-3 py-2">
      <div className={`font-display font-extrabold text-lg tabular-nums ${tone}`}>{value}</div>
      <div className="font-mono text-[10px] text-[#4B5563] uppercase tracking-widest mt-0.5">{label}</div>
    </div>
  );
}
